import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useAdminAuth } from '../context/AdminAuthContext';
import Sidebar from './Sidebar';
import '../styles/admin.css';

interface AdminLayoutProps {
  title: string;
  children: React.ReactNode;
}

const LEVEL_LABEL: Record<string, string> = {
  admin: 'Administrador',
  super_admin: 'Super Administrador',
};

const AdminLayout: React.FC<AdminLayoutProps> = ({ title, children }) => {
  const { userLevel, logoutAdmin } = useAdminAuth();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const handleLogout = () => {
    logoutAdmin();
    navigate('/rj/admin-login');
  };

  return (
    <div className="admin-shell">
      <div className="admin-shell__sidebar admin-shell__sidebar--desktop">
        <Sidebar />
      </div>

      <AnimatePresence>
        {isSidebarOpen && (
          <React.Fragment key="mobile-sidebar">
            <motion.div
              className="admin-shell__overlay"
              onClick={() => setIsSidebarOpen(false)}
              aria-hidden="true"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            />
            <motion.div
              className="admin-shell__sidebar admin-shell__sidebar--mobile"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
            >
              <Sidebar onNavigate={() => setIsSidebarOpen(false)} />
            </motion.div>
          </React.Fragment>
        )}
      </AnimatePresence>

      <div className="admin-content">
        <header className="admin-topbar">
          <button
            type="button"
            className="admin-topbar__menu-toggle"
            onClick={() => setIsSidebarOpen((v) => !v)}
            aria-label="Alternar barra lateral"
          >
            <span />
            <span />
            <span />
          </button>

          <h1 className="admin-topbar__title">{title}</h1>

          <div className="admin-topbar__actions">
            {userLevel && (
              <span className="admin-level-badge">{LEVEL_LABEL[userLevel] || userLevel}</span>
            )}
            <button
              type="button"
              onClick={handleLogout}
              className="admin-logout-button"
              aria-label="Sair do painel de administração"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Sair
            </button>
          </div>
        </header>

        <main className="admin-content__body">{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;
